import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'

import { decimalDisplay } from 'utilities/pricing'
import OrderSummaryItem from 'components/OrderSummaryItem'
import OrderSummaryItemLarge from 'components/OrderSummaryItemLarge'
import {
  STAGE_PLAN_SELECT,
  STAGE_MODEM_SELECT,
  STAGE_PERSONAL,
  STAGE_IDENTIFICATION,
  STAGE_PAYMENT,
  STAGE_SUCCESS,
  STAGES,
} from 'containers/Checkout/constants'
import { transitionToStage } from 'containers/Checkout/actions'
import OrderSummaryTotal from 'components/OrderSummaryTotal'
import { calculateUpfront } from 'containers/stages/PaymentStage/utils'

import styles from './styles.scss'

class OrderSummary extends PureComponent {
  static propTypes = {
    currentStage: PropTypes.string,
    installAddress: PropTypes.arrayOf(PropTypes.string),
    identificationVerified: PropTypes.bool,
    detailsProvided: PropTypes.shape({
      first_name: PropTypes.string,
      middle_name: PropTypes.string,
      last_name: PropTypes.string,
      phone: PropTypes.string,
    }),
    deliveryAddress: PropTypes.string,
    planSelected: PropTypes.shape({
      interval: PropTypes.string,
      marketingTagline: PropTypes.string,
      rate: PropTypes.number,
      title: PropTypes.string,
      orderSummaryDisclaimer: PropTypes.string,
      planColor: PropTypes.string,
    }),
    totalUpfront: PropTypes.number,
    stageSettings: PropTypes.shape({
      identification: PropTypes.string,
      landline_check: PropTypes.string,
      modem_delivery: PropTypes.string,
      payment: PropTypes.string,
      personal: PropTypes.string,
      plan_select: PropTypes.string,
    }),
    summarySettings: PropTypes.object,
    email: PropTypes.string,
    modemRequired: PropTypes.bool,
    transitionToStage: PropTypes.func,
  }

  static defaultProps = {
    currentStage: '',
    installAddress: [],
    identificationVerified: false,
    deliveryAddress: '',
    planSelected: {
      interval: '',
      marketingTagline: '',
      rate: 0,
      title: '',
      orderSummaryDisclaimer: '',
    },
    totalUpfront: 0,
    stageSettings: {
      identification: 'Identification',
      landline_check: 'Install Address',
      modem_delivery: 'Modem',
      payment: 'Payment',
      personal: 'Your Details',
      modem_select: 'Modem',
      plan_select: 'Plan',
    },
    summarySettings: { costHeading: 'Total Cost' },
    detailsProvided: {
      first_name: '',
      middle_name: '',
      last_name: '',
      phone: '',
    },
    email: '',
    modemRequired: false,
    transitionToStage: f => f,
  }

  constructor(props) {
    super(props)
    this.state = { collapsed: true }
  }

  getCollapsedButtonText = () =>
    (this.state.collapsed ? 'Order Details' : 'Hide Details')

  isStageEditable = (targetStage) => {
    const currentStageIndex = STAGES.findIndex(s => s.id === this.props.currentStage)
    const targetStageIndex = STAGES.findIndex(s => s.id === targetStage)
    return (targetStageIndex < currentStageIndex) && (targetStageIndex > -1)
  }

  identificationVerified = () => {
    const verifiedAndCorrectStage =
      this.props.identificationVerified &&
      this.props.currentStage !== STAGE_IDENTIFICATION
    return verifiedAndCorrectStage ? 'Verified' : null
  }

  purchaseCostShouldShow = () => {
    const { summarySettings } = this.props

    const shouldShow =
      this.props.planSelected &&
      (this.props.currentStage === STAGE_PAYMENT ||
        this.props.currentStage === STAGE_SUCCESS)
    return shouldShow ? summarySettings.costHeading : null
  }

  leadDetailsProvided = () => {
    /* Mandatory fields provided and not on personal details page */
    const {
      detailsProvided: {
        first_name: firstName,
        middle_name: middleName,
        last_name: lastName,
      },
    } = this.props
    const fullName = [firstName, middleName, lastName].filter(x => !!x).join(' ')
    return fullName.length > 1 ? fullName : null
  }

  toggleCollapsed = () => {
    this.setState({ collapsed: !this.state.collapsed })
  }

  render() {
    const {
      currentStage,
      installAddress,
      planSelected,
      deliveryAddress,
      stageSettings,
      summarySettings,
      email,
      modemRequired,
      totalUpfront,
    } = this.props

    const { costText, planInfoText, modemInfoText } = summarySettings

    const planRate = decimalDisplay(planSelected.rate)
    const totalDisplay = decimalDisplay(totalUpfront)

    const wrapperExpandedStyle = !this.state.collapsed ? styles.wrapperExpanded : ''
    const summaryExpandedStyle = !this.purchaseCostShouldShow() ? '' : styles.collapsed
    const installAddressStreet = installAddress.length >= 1 && installAddress[0]
      ? `${installAddress[0]},`
      : null
    const installAddressCity = installAddress.length >= 2 && installAddress[1]
      ? installAddress[1]
      : null

    const installAddressCollapsedViewStyle = this.state.collapsed ? styles.installAddress : ''
    const planCollapsedViewStyle = this.state.collapsed
      ? `${styles.selectedPlan} ${styles.selectedPlan__collapsed_view}`
      : styles.selectedPlan
    const deliveryAddressSplit = deliveryAddress.split(',')
    const disclaimers = (
      planSelected.orderSummaryDisclaimer ?
        <div dangerouslySetInnerHTML={{ __html: planSelected.orderSummaryDisclaimer }} /> :
        null
    )

    // Don't show the mobile summary button until there is enough information.
    // Mostly used for NBN leads that dont have contact details yet (location ID referrals).
    const showMobileExpander = (
      !this.state.collapsed ||
      (this.props.detailsProvided && this.props.detailsProvided.first_name) ||
      (this.props.planSelected && this.props.planSelected.title)
    )

    return (
      <div className={`${styles.breakout} ${styles.boxShadow}`}>
        <div className={styles.wrapper} data-stage={currentStage}>
          <div className={`${styles.scrollWrapper} ${wrapperExpandedStyle}`}>
            <div className={summaryExpandedStyle}>
              <OrderSummaryItem
                extraClass={`${styles.stepSummaryItem} ${installAddressCollapsedViewStyle}`}
                icon="house"
                title={stageSettings.landline_check}
                primaryInfo={installAddressStreet}
                secondaryInfo={installAddressCity}
              />
              <OrderSummaryItem
                extraClass={`${styles.stepSummaryItem} ${planCollapsedViewStyle} ${styles.hidePlanIconMobile}`}
                icon="plan"
                title={stageSettings.plan_select}
                primaryInfo={planSelected.title}
                secondaryInfo={planSelected.marketingTagline}
                stageEditable={this.isStageEditable(STAGE_PLAN_SELECT)}
                onClick={() => this.props.transitionToStage(STAGE_PLAN_SELECT)}
              />
              { modemRequired &&
                <OrderSummaryItem
                  icon="truck"
                  title={stageSettings.modem_delivery}
                  extraClass={`${styles.stepSummaryItem}`}
                  primaryInfo={deliveryAddressSplit[0]}
                  secondaryInfo={deliveryAddressSplit[1]}
                  stageEditable={this.isStageEditable(STAGE_MODEM_SELECT)}
                  onClick={() => this.props.transitionToStage(STAGE_MODEM_SELECT)}
                />
              }
              <OrderSummaryItem
                icon="person"
                title={stageSettings.personal}
                extraClass={`${styles.stepSummaryItem} ${styles.personalDetails}`}
                primaryInfo={this.leadDetailsProvided()}
                secondaryInfo={email}
                stageEditable={this.isStageEditable(STAGE_PERSONAL)}
                onClick={() => this.props.transitionToStage(STAGE_PERSONAL)}
              />
              <OrderSummaryItem
                icon="identification"
                title={stageSettings.identification}
                extraClass={`${styles.stepSummaryItem} ${styles.identification}`}
                primaryInfo={this.identificationVerified()}
                stageEditable={this.isStageEditable(STAGE_IDENTIFICATION)}
                onClick={() => this.props.transitionToStage(STAGE_IDENTIFICATION)}
              />
            </div>
            <OrderSummaryTotal
              title={this.purchaseCostShouldShow()}
              headingClass={styles.heading}
              extraClass={styles.totalCost}
              primaryInfo={costText}
              planRate={planRate}
              planUpfront={totalDisplay}
              planInterval={planSelected.interval}
            >
              <OrderSummaryItemLarge
                icon="plan"
                iconColor={planSelected.planColor}
                title={planSelected.title}
                subtitle={planSelected.marketingTagline}
                primaryInfo={`$${planRate}`}
                secondaryInfo={planInfoText}
                extraClass={styles.summaryItemLarge}
              >
                {disclaimers}
              </OrderSummaryItemLarge>
              <OrderSummaryItemLarge
                secondaryInfo={modemInfoText}
                extraClass={styles.summaryItemLarge}
              />
            </OrderSummaryTotal>
            {showMobileExpander && (
              <button
                className={styles.hyperlinkButton}
                onClick={this.toggleCollapsed}
              >
                {this.getCollapsedButtonText()}
              </button>
            )}
            <div className="clearfix" />
          </div>
        </div>
      </div>
    )
  }
}

function mapStateToProps(state) {
  // whatever is returned will show up as props
  return {
    currentStage: state.checkoutReducer.currentStage,
    installAddress: state.checkoutReducer.installAddress,
    identificationVerified: state.checkoutReducer.identityVerified,
    deliveryAddress: state.checkoutReducer.modemDeliveryAddress,
    detailsProvided: state.checkoutReducer.personalFieldValues,
    planSelected: state.checkoutReducer.planSelected,
    email: state.checkoutReducer.email,
    modemRequired: state.checkoutReducer.modemRequired,
    totalUpfront: calculateUpfront(state),
  }
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({ transitionToStage }, dispatch)
}

export default connect(mapStateToProps, mapDispatchToProps)(OrderSummary)



// WEBPACK FOOTER //
// ./components/OrderSummary/index.jsx